import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "admin/common/icons/CloseIcon";
import SearchIcon from "admin/common/icons/SearchIcon";
import TableLoader from "admin/common/TableLoader";
import Apiauth from "API/Apiauth";
import ZoneIndexGridView from "components/zonemodule/ZoneIndexGridView";
import ZoneIndexSummaryView, {
  ZoneIndexSummaryBulkAiButton,
  ZoneIndexSummaryCollapseToggle,
  ZoneIndexSummarySelectionBar,
} from "components/zonemodule/ZoneIndexSummaryView";
import { getLocationId } from "components/zonemodule/zoneIndexGridUtils";
import commonFilterIcon from "assets/images/commonfiltericon.svg";
import zoneSummaryIcon from "assets/images/zonesummaryicon.svg";
import zoneSummaryIconWhite from "assets/images/zonesummaryiconwhite.svg";
import "components/zonemodule/ZoneIndex.scss";
import "./PosZonePage.scss";

const getPosLocationId = () => {
  try {
    const raw = localStorage.getItem("pos-loc");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return String(parsed?.loc ?? "");
  } catch (err) {
    console.warn("Invalid pos-loc in localStorage", err);
    return "";
  }
};

const PosZonePage = () => {
  const { t } = useTranslation();
  const filterRef = useRef(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftSoldOut, setDraftSoldOut] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({
    locations: [],
    soldOut: [],
  });
  const [isSummaryActive, setIsSummaryActive] = useState(false);
  const [summaryAllExpanded, setSummaryAllExpanded] = useState(true);
  const [summaryCollapseResetKey, setSummaryCollapseResetKey] = useState(0);
  const [summaryBulkAiToolbar, setSummaryBulkAiToolbar] = useState(null);
  const [summarySelectionToolbar, setSummarySelectionToolbar] = useState(null);
  const posLocationId = getPosLocationId();

  const loadZones = useCallback(async () => {
    setLoading(true);
    try {
      const apiRes = await Apiauth({}, "zone/all-zone");
      if (apiRes?.code === 200) {
        setLocations(apiRes?.data?.locations || []);
      }
    } catch (err) {
      console.error(err, "API Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const posLocations = useMemo(() => {
    if (!posLocationId) return [];
    return (locations || []).filter(
      (location) => getLocationId(location) === posLocationId
    );
  }, [locations, posLocationId]);

  useEffect(() => {
    if (!isSummaryActive) {
      setSummaryBulkAiToolbar(null);
      setSummarySelectionToolbar(null);
    }
  }, [isSummaryActive]);

  useEffect(() => {
    if (isSummaryActive) {
      setSummaryAllExpanded(true);
      setSummaryCollapseResetKey((key) => key + 1);
    }
  }, [isSummaryActive]);

  const handleSummaryCollapseToggle = () => {
    setSummaryAllExpanded((expanded) => !expanded);
    setSummaryCollapseResetKey((key) => key + 1);
  };

  const appliedFilterCount = appliedFilters.soldOut.length;

  const toggleFilterPopup = () => {
    if (isFilterOpen) {
      setIsFilterOpen(false);
      return;
    }

    setDraftSoldOut([...(appliedFilters.soldOut || [])]);
    setIsFilterOpen(true);
  };

  const handleFilterApply = () => {
    setAppliedFilters({
      locations: [],
      soldOut: [...draftSoldOut],
    });
    setIsFilterOpen(false);
  };

  const handleFilterClear = () => {
    setDraftSoldOut([]);
    setAppliedFilters({ locations: [], soldOut: [] });
    setIsFilterOpen(false);
  };

  const handleSearchClose = () => {
    setSearchQuery("");
    setOpenSearch(false);
  };

  useEffect(() => {
    if (!isFilterOpen) return undefined;

    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  return (
    <div className="main-body pos-zone-page">
      <div className="pos-zone-page__content zone-index">
        <div className="pos-zone-page__toolbar">
          <div className="pos-zone-page__toolbar-left">
            {isSummaryActive && summarySelectionToolbar?.hasSelectedZones ? (
              <ZoneIndexSummarySelectionBar
                selectedCount={summarySelectionToolbar.selectedCount}
                isAllSelected={summarySelectionToolbar.isAllSelected}
                onSelectAllChange={summarySelectionToolbar.onSelectAllChange}
                onClear={summarySelectionToolbar.onClear}
                t={t}
              />
            ) : (
              <>
                <h2 className="pos-zone-page__title">{t("Zones")}</h2>
                <button
                  type="button"
                  className={`zone-index__summary-btn${
                    isSummaryActive ? " zone-index__summary-btn--active" : ""
                  }`}
                  aria-pressed={isSummaryActive}
                  onClick={() => setIsSummaryActive((active) => !active)}
                >
                  <span className="zone-index__summary-btn-icon-wrap" aria-hidden>
                    <img
                      src={isSummaryActive ? zoneSummaryIconWhite : zoneSummaryIcon}
                      alt=""
                      className="zone-index__summary-btn-icon"
                    />
                  </span>
                  <span className="zone-index__summary-btn-label">
                    {t("Summary")}
                  </span>
                </button>
              </>
            )}
          </div>

          <div className="pos-zone-page__toolbar-actions">
            {isSummaryActive ? (
              <>
                <ZoneIndexSummaryBulkAiButton
                  hasSelectedZones={summaryBulkAiToolbar?.hasSelectedZones ?? false}
                  isLoading={summaryBulkAiToolbar?.isLoading ?? false}
                  onClick={summaryBulkAiToolbar?.onClick}
                  t={t}
                />
                <ZoneIndexSummaryCollapseToggle
                  isExpanded={summaryAllExpanded}
                  onToggle={handleSummaryCollapseToggle}
                  t={t}
                />
              </>
            ) : null}

            <div className="zone-index__filter-wrap" ref={filterRef}>
              <button
                type="button"
                className="zone-index__icon-btn zone-index__filter-btn"
                onClick={toggleFilterPopup}
                aria-label={
                  appliedFilterCount > 0
                    ? t("Filter ({{count}} active)", {
                        count: appliedFilterCount,
                      })
                    : t("Filter")
                }
                aria-expanded={isFilterOpen}
              >
                <img src={commonFilterIcon} alt="" aria-hidden />
                {appliedFilterCount > 0 ? (
                  <span className="zone-index__filter-btn-badge">
                    {appliedFilterCount}
                  </span>
                ) : null}
              </button>

              {isFilterOpen ? (
                <div
                  className="zone-index__filter-popup pos-zone-page__filter-popup"
                  role="dialog"
                  aria-label={t("Filter")}
                >
                  <div className="zone-index__filter-popup-title">
                    {t("Filter")}
                  </div>
                  <div className="zone-index__filter-popup-divider" />
                  <div className="zone-index__filter-active-row">
                    <span className="zone-index__filter-active-count">
                      {t("Active Filters")} ({draftSoldOut.length})
                    </span>
                    <button
                      type="button"
                      className="zone-index__filter-clear-all"
                      onClick={handleFilterClear}
                    >
                      {t("Clear all")}
                    </button>
                  </div>
                  <div className="zone-index__filter-popup-divider" />
                  <div className="zone-index__filter-popup-body">
                    <div className="zone-index__filter-category">
                      <div className="zone-index__filter-category-header">
                        <span className="zone-index__filter-category-title">
                          {t("Sold out")}
                        </span>
                      </div>
                      <div className="zone-index__filter-pills">
                        <button
                          type="button"
                          className={`zone-index__filter-pill${
                            draftSoldOut.includes("yes")
                              ? " zone-index__filter-pill--selected"
                              : ""
                          }`}
                          onClick={() =>
                            setDraftSoldOut((current) =>
                              current.includes("yes") ? [] : ["yes"]
                            )
                          }
                        >
                          {t("Yes")}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="zone-index__filter-apply-btn"
                    onClick={handleFilterApply}
                  >
                    {t("Apply")}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="zone-index__search-wrap">
              {!openSearch ? (
                <button
                  type="button"
                  className="zone-index__icon-btn"
                  onClick={() => setOpenSearch(true)}
                  aria-label={t("Search")}
                >
                  <SearchIcon color="#A4A4A4" />
                </button>
              ) : null}
              <div
                className={`zone-index__search-bar${
                  openSearch ? " is-open" : ""
                }`}
              >
                <input
                  type="text"
                  value={searchQuery}
                  className="zone-index__search-input"
                  placeholder={t("Search")}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
                <button
                  type="button"
                  className="zone-index__icon-btn"
                  onClick={handleSearchClose}
                  aria-label={t("Close search")}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="pos-zone-page__loader">
            <TableLoader />
          </div>
        ) : isSummaryActive ? (
          <ZoneIndexSummaryView
            locations={posLocations}
            appliedFilters={appliedFilters}
            searchQuery={searchQuery}
            allLocationsExpanded={summaryAllExpanded}
            collapseResetKey={summaryCollapseResetKey}
            onBulkAiToolbarChange={setSummaryBulkAiToolbar}
            onSelectionToolbarChange={setSummarySelectionToolbar}
            t={t}
            disableIssueNavigation
          />
        ) : (
          <ZoneIndexGridView
            locations={posLocations}
            appliedFilters={appliedFilters}
            searchQuery={searchQuery}
            t={t}
            disableIssueNavigation
          />
        )}
      </div>
    </div>
  );
};

export default PosZonePage;
